import React, { useEffect, useState } from 'react';
import CollapsiblePatientCard from '../CollapsiblePatientCard';
import PatientHistory from '../PatientHistory';
import { Save, Plus, FileText, Calendar, Stethoscope, X, ClipboardList, AlertCircle, Pill, Activity, ChevronRight, ChevronLeft } from 'lucide-react';

// Array Input Component for managing lists (allergies, medications, etc.)
const ArrayInputField = ({ label, items = [], onAdd, onRemove, placeholder, helperText, colorScheme = 'teal' }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  // Color schemes for different types
  const colorSchemes = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      tagBg: 'bg-red-100',
      tagText: 'text-red-800',
      tagHover: 'hover:bg-red-200',
      button: 'bg-red-600 hover:bg-red-700'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      tagBg: 'bg-orange-100',
      tagText: 'text-orange-800',
      tagHover: 'hover:bg-orange-200',
      button: 'bg-orange-600 hover:bg-orange-700'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      tagBg: 'bg-green-100',
      tagText: 'text-green-800',
      tagHover: 'hover:bg-green-200',
      button: 'bg-green-600 hover:bg-green-700'
    },
    teal: {
      bg: 'bg-[#9DC9D4]/20',
      border: 'border-[#9DC9D4]',
      tagBg: 'bg-[#9DC9D4]/30',
      tagText: 'text-[#2D869C]',
      tagHover: 'hover:bg-[#9DC9D4]/50',
      button: 'bg-[#2D869C] hover:bg-[#236b7a]'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      tagBg: 'bg-blue-100',
      tagText: 'text-blue-800',
      tagHover: 'hover:bg-blue-200',
      button: 'bg-blue-600 hover:bg-blue-700'
    }
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.teal;

  return (
    <div className="space-y-2">
      <label className="block text-sm text-gray-500 uppercase font-medium">
        {label}
      </label>
      {helperText && (
        <p className="text-sm text-gray-500 -mt-1">{helperText}</p>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all text-base"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          className={`px-3 py-2 ${colors.button} text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-all`}
        >
          <Plus size={18} />
        </button>
      </div>
      {items && items.length > 0 && (
        <div className={`flex flex-wrap gap-2 p-2 ${colors.bg} ${colors.border} border rounded-lg`}>
          {items.map((item, index) => (
            <span
              key={index}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${colors.tagBg} ${colors.tagText} rounded-full text-sm font-medium`}
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => onRemove(index)}
                className={`${colors.tagHover} rounded-full p-0.5 transition-colors`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const FrontPage = ({
  selectedPatient,
  patientData,
  setPatientData,
  loading,
  onClearSelection,
  onSavePatient,
  updateAppointmentComplaint,
  currentAppointmentId,
  onPatientDataRefresh,
  onVitalsChangeStart,
  onVitalsSaved,
  onPatientDetailsChangeStart,
  onPatientDetailsSaved
}) => {

  // Local state for input fields to allow proper editing (space, ctrl+z, etc.)
  const [drugAllergiesText, setDrugAllergiesText] = useState('');
  const [generalAllergiesText, setGeneralAllergiesText] = useState('');
  const [currentMedicationsText, setCurrentMedicationsText] = useState('');
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);

  // Initialize local state when patientData changes
  useEffect(() => {
    if (patientData?.drugAllergies) {
      setDrugAllergiesText(Array.isArray(patientData.drugAllergies) ? patientData.drugAllergies.join(', ') : '');
    }
    if (patientData?.allergies) {
      setGeneralAllergiesText(Array.isArray(patientData.allergies) ? patientData.allergies.join(', ') : '');
    }
    if (patientData?.currentMedications) {
      setCurrentMedicationsText(Array.isArray(patientData.currentMedications) ? patientData.currentMedications.join(', ') : '');
    }
  }, [patientData?.drugAllergies, patientData?.allergies, patientData?.currentMedications]);

  // Handle blur to save the parsed array values
  const handleDrugAllergiesBlur = () => {
    const parsed = drugAllergiesText.split(',').map(s => s.trim()).filter(Boolean);
    handleInputChange('drugAllergies', parsed);
  };

  const handleGeneralAllergiesBlur = () => {
    const parsed = generalAllergiesText.split(',').map(s => s.trim()).filter(Boolean);
    handleInputChange('allergies', parsed);
  };

  const handleCurrentMedicationsBlur = () => {
    const parsed = currentMedicationsText.split(',').map(s => s.trim()).filter(Boolean);
    handleInputChange('currentMedications', parsed);
  };

  // Note: Complaint is now loaded directly in loadPatientData from appointment API
  // No need for useEffect here as it's handled in the hook

  const handleInputChange = (field, value) => {
    setPatientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle array field updates (for allergies, medications, etc.)
  const handleArrayAdd = (field, item) => {
    setPatientData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), item]
    }));
  };

  const handleArrayRemove = (field, index) => {
    setPatientData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-4 relative" style={{ fontFamily: ' Poppins, sans-serif' }}>
      {/* Top Section: Grid Layout */}
      <div className={`grid gap-4 transition-all duration-300 ${
        isHistoryCollapsed ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-4'
      }`}>
        {/* Left Section: Patient Card + Forms */}
        <div className={`space-y-4 ${
          isHistoryCollapsed ? '' : 'xl:col-span-3'
        }`}>
          <CollapsiblePatientCard
            selectedPatient={selectedPatient}
            patientData={patientData}
            onVitalsChangeStart={onVitalsChangeStart}
            onVitalsSaved={onVitalsSaved}
            onPatientDetailsChangeStart={onPatientDetailsChangeStart}
            onPatientDetailsSaved={onPatientDetailsSaved}
            onPatientUpdate={(updatedData) => {
              setPatientData(prev => ({
                ...prev,
                ...updatedData
              }));
              
              const updatedKeys = Object.keys(updatedData || {});
              const isVitalsOnly = updatedKeys.length === 1 && updatedKeys[0] === 'vitals';
              if (!isVitalsOnly && onPatientDataRefresh) {
                onPatientDataRefresh();
              }
            }}
          />

          {/* Forms Section - Split into 2 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Box: Complaint + Physical Exam */}
            <div className="space-y-4">
              {/* Chief Complaint Card */}
              <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                <div className="bg-[#2D869C] px-4 py-2.5 flex items-center gap-2">
                  <ClipboardList size={20} className="text-white" />
                  <h3 className="text-white font-semibold text-xl">Chief Complaint</h3>
                  <span className="text-red-300 text-xl ml-auto">*Required</span>
                </div>
                <div className="p-3">
                  <textarea
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] resize-none text-black font-semibold text-xl transition-all"
                    rows="6"
                    placeholder="Enter the main reason for patient's visit..."
                    value={patientData?.chiefComplaint || ''}
                    onChange={(e) => handleInputChange('chiefComplaint', e.target.value)}
                  />
                </div>
              </div>

              {/* Physical Examination Card */}
              <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                <div className="bg-[#2D869C] px-4 py-2.5 flex items-center gap-2">
                  <Activity size={20} className="text-white" />
                  <h3 className="text-white font-semibold text-xl">Physical Examination</h3>
                </div>
                <div className="p-3">
                  <textarea
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] resize-none text-black font-semibold text-xl transition-all"
                    rows="6"
                    placeholder="General appearance, system-wise examination findings..."
                    value={patientData?.physicalExamination || ''}
                    onChange={(e) => handleInputChange('physicalExamination', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Right Box: Medical Background */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden h-fit">
              <div className="bg-[#2D869C] px-4 py-2.5 flex items-center gap-2">
                <FileText size={20} className="text-white" />
                <h3 className="text-white font-semibold text-xl">Medical Background</h3>
              </div>
              <div className="p-3 space-y-3">
                {/* Past Medical History */}
                <div>
                  <label className="block text-xl text-black font-semibold  mb-1">
                    Past Medical History
                  </label>
                  <textarea
                    className="w-full px-2.5 py-1.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] resize-none text-black font-semibold bg-gray-50 text-xl transition-all"
                    rows="5"
                    placeholder="Previous conditions, surgeries, hospitalizations..."
                    value={patientData?.pastHistory || patientData?.pastMedicalHistory || ''}
                    onChange={(e) => handleInputChange('pastHistory', e.target.value)}
                  />
                </div>

                {/* Allergies - Stacked */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-red-50/50 rounded-lg p-2 border border-red-100">
                    <div className="flex items-center gap-1 mb-1">
                      <AlertCircle size={20} className="text-red-500" />
                      <label className="text-xl text-black font-semibold">Drug Allergies</label>
                    </div>
                    <input
                      type="text"
                      className="w-full px-2 py-2.5 border border-gray-200 rounded focus:ring-1 focus:ring-[#9DC9D4] focus:border-[#2D869C] bg-white text-black font-semibold text-xl"
                      placeholder="e.g., Penicillin"
                      value={drugAllergiesText}
                      onChange={(e) => setDrugAllergiesText(e.target.value)}
                      onBlur={handleDrugAllergiesBlur}
                    />
                  </div>
                  <div className="bg-orange-50/50 rounded-lg p-2 border border-orange-100">
                    <div className="flex items-center gap-1 mb-1">
                      <AlertCircle size={20} className="text-orange-500" />
                      <label className="text-xl text-black font-semibold">General Allergies</label>
                    </div>
                    <input
                      type="text"
                      className="w-full px-2 py-2.5 border border-gray-200 rounded focus:ring-1 focus:ring-[#9DC9D4] focus:border-[#2D869C] bg-white text-black font-semibold text-xl"
                      placeholder="e.g., Peanuts"
                      value={generalAllergiesText}
                      onChange={(e) => setGeneralAllergiesText(e.target.value)}
                      onBlur={handleGeneralAllergiesBlur}
                    />
                  </div>
                </div>

                {/* Current Medications */}
                <div className="bg-[#9DC9D4]/10 rounded-lg p-2 border border-[#9DC9D4]/30">
                  <div className="flex items-center gap-1 mb-1">
                    <Pill size={20} className="text-[#2D869C]" />
                    <label className="text-xl text-black font-semibold">Current Medications</label>
                  </div>
                  <textarea
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#9DC9D4] focus:border-[#2D869C] bg-white text-black font-semibold text-xl resize-none"
                    rows="3"
                    placeholder="e.g., Lisinopril 10mg - Once daily"
                    value={currentMedicationsText}
                    onChange={(e) => setCurrentMedicationsText(e.target.value)}
                    onBlur={handleCurrentMedicationsBlur}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section: Patient History - Collapsible */}
        {!isHistoryCollapsed && (
          <div className="xl:col-span-1 relative">
            <PatientHistory selectedPatient={selectedPatient} />
          </div>
        )}
      </div>

      {/* Toggle Button for Patient History */}
      <button
        onClick={() => setIsHistoryCollapsed(!isHistoryCollapsed)}
        className="fixed right-7 top-28 z-20 bg-[#2D869C] text-white p-2 rounded-l-lg shadow-lg hover:bg-[#236b7a] transition-all"
        style={{ 
          marginRight: isHistoryCollapsed ? '0' : '420px',
          transition: 'margin-right 0.3s ease'
        }}
        title={isHistoryCollapsed ? 'Show Patient History' : 'Hide Patient History'}
      >
        {isHistoryCollapsed ? (
          <ChevronLeft size={20} />
        ) : (
          <ChevronRight size={20} />
        )}
      </button>

      {/* Action Buttons - Right Aligned Footer */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
        <button
          onClick={onClearSelection}
          className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-6 py-2.5 rounded-lg font-medium transition-colors border-2 border-gray-200 text-base"
        >
          <X size={20} className="text-red-700" />
          <span className="text-xl font-semibold">Clear Form</span>
        </button>

        <button
          onClick={onSavePatient}
          disabled={loading}
          className="flex items-center justify-center gap-2 bg-[#2D869C] hover:bg-[#236b7a] disabled:bg-gray-400 text-white px-8 py-2.5 rounded-lg font-semibold transition-colors text-base shadow-sm"
        >
          <Save size={20} />
          <span className="text-xl font-semibold">{loading ? 'Saving...' : 'Save Patient Information'}</span>
        </button>
      </div>
    </div>
  );
};

export default FrontPage;
